
import { 
  IEndpointRequest, 
  IGetEndpointRequest,
  IPostEndpointRequest,
  IPutEndpointRequest,
} from "@/types/definitions/apiCalls";
import * as Sentry from "@sentry/browser";
import axios, { AxiosResponse } from "axios";

const URL_PREFIX = import.meta.env.testURL ?? "/";

const API_PREFIX = "api/";

export type APIResponseType<TResponse> = Promise<{promise: AxiosResponse<TResponse>, version: number}>;

class API {

  static #constructEndpoint(
    route: string, 
    version: number, 
    params?: Record<string, unknown>,
    searchParams?: string
  ) {
    let constructedRoute = route;
    for (const param in params) {
      constructedRoute = constructedRoute.replace(`:${param}`, params[param] as string);
    }
    return [URL_PREFIX, API_PREFIX, `v${version}`, constructedRoute, searchParams].join('');
  }

  static async #doRequest<TResponse>({
    method,
    endpoint,
    versions,
    currentVersion = 1,
    params,
    searchParams, 
    config,
    data
  }: IEndpointRequest): APIResponseType<TResponse> {
    try {
      switch(method) {
        case "GET":
          return {
            promise: await axios.get<TResponse>(this.#constructEndpoint(endpoint, currentVersion, params, searchParams), config),
            version: currentVersion
          };
        case "PUT":
          return {
            promise: await axios.put<TResponse>(this.#constructEndpoint(endpoint, currentVersion, params, searchParams), data, config),
            version: currentVersion
          };
        case "POST":
          return {
            promise: await axios.post<TResponse>(this.#constructEndpoint(endpoint, currentVersion, params, searchParams), data, config),
            version: currentVersion
          };
        default:
          throw new Error("El method demanat no existeix");
      }

    } catch (err) {
      if(currentVersion-1 > 0) {
        return this.#doRequest({
          method,
          endpoint,
          versions,
          currentVersion: currentVersion-1,
          params,
          searchParams,
          config,
          data
        });
      } else {
        Sentry.captureException(err);
        throw err
      } 
    }
  }

  static async get<TResponse>({
    endpoint, 
    versions,
    params,
    searchParams,
    config
  }: IGetEndpointRequest) {
    try {
      // const validEndpoint = await this.#validateEndpoint({
      //   endpoint, method: "GET", versions
      // });
      return await this.#doRequest<TResponse>({
        endpoint, method: "GET", versions, 
        ...(Array.isArray(versions) && {currentVersion: Math.max(...versions)}),
        params, searchParams, config
      });

    } catch (err) {
      throw err;
    }
  }

  static async put<TResponse, TData>({
    endpoint,
    versions,
    params,
    searchParams,
    config,
    data
  }: IPutEndpointRequest<TData>) {
    try {
      // const validEndpoint = await this.#validateEndpoint({
      //   endpoint, method: "PUT", versions
      // });
      return await this.#doRequest<TResponse>({
        endpoint, method: "PUT", versions,
        ...(Array.isArray(versions) && {currentVersion: Math.max(...versions)}),
        params, searchParams, config, data
      });

    } catch (err) {
      throw err;
    }
  }

  static async post<TResponse, TData>({
    endpoint,
    versions,
    params,
    searchParams,
    config,
    data
  }: IPostEndpointRequest<TData>) {
    try {
      // const validEndpoint = await this.#validateEndpoint({
      //   endpoint, method: "POST", versions
      // });
      const response = await this.#doRequest<TResponse>({
        endpoint, method: "POST", versions,
        ...(Array.isArray(versions) && {currentVersion: Math.max(...versions)}),
        params, searchParams, config, data
      });
      return response;
    } catch (err) {
      throw err;
    }
  }
}


export default API;